import { Component, Input, OnDestroy, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { Dialog } from "@angular/cdk/dialog";
import { ImagesModalComponent } from "../images-modal/images-modal.component";
import { BqaImageItem } from "../images-modal/bqa-image-item";
import { Media } from "../../../../core/database/db";
import { BlobService } from "../../../../shared/services/blob.service";
import { OnlineImages } from "../../../reports/models/BQ7ReportResponseDTO";

@Component({
  selector: "app-assertion-image-gallery",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./assertion-image-gallery.component.html",
  styleUrls: ["./assertion-image-gallery.component.scss"]
})
export class AssertionImageGalleryComponent implements OnDestroy {
  @Input() images: (Media & OnlineImages)[] = [];
  @Input() readonly: boolean = false;
  @Input() isComplete: boolean = false;
  /**
   * Optional fixed thumbnail size, in px. When set, the gallery renders a compact wrapping grid of
   * squares of this size instead of the default responsive two-column layout.
   */
  @Input() thumbnailSize?: number;
  /** When set, only this many thumbnails render and the rest collapse into a trailing "+N" tile. */
  @Input() maxThumbnails?: number;

  get visibleImages(): (Media & OnlineImages)[] {
    return this.maxThumbnails ? this.images.slice(0, this.maxThumbnails) : this.images;
  }

  get hiddenImageCount(): number {
    return Math.max(0, this.images.length - this.visibleImages.length);
  }


  private dialog = inject(Dialog);
  private sanitizer = inject(DomSanitizer);
  private blobService = inject(BlobService);
  private blobUrlCache = new Map<string, string>();
  private blobUrls: string[] = [];

  getImageUrl(media: (Media & OnlineImages)): SafeUrl {
    if (media.base64Image) {
      const key = this.getCacheKey(media);
      let url = this.blobUrlCache.get(key);
      if (!url) {
        url = URL.createObjectURL(this.blobService.base64ToBlob(media.base64Image));
        this.blobUrlCache.set(key, url);
        this.blobUrls.push(url);
      }
      return this.sanitizer.bypassSecurityTrustUrl(url);
    } else if (media.azureBlobUri) {
      return this.sanitizer.bypassSecurityTrustUrl(media.azureBlobUri);
    } else if (media.resourceUri) {
      return media.resourceUri;
    }

    return "";
  }

  openImagesModal(index: number): void {
    const galleryItems: BqaImageItem[] = this.images
      .filter(image => !!image.base64Image || !!image.resourceUri || !!image.azureBlobUri)
      .map(image => {
        // Reuse existing resourceUri if present; otherwise ensure cached blob URL
        const existingResourceUri = image.resourceUri;
        const shouldUseExistingResourceUri = !!existingResourceUri && existingResourceUri.startsWith("http");
        let src = shouldUseExistingResourceUri ? existingResourceUri : "";

        if (shouldUseExistingResourceUri === false && image.base64Image) {
          const key = this.getCacheKey(image);
          let cached = this.blobUrlCache.get(key);
          const hasCachedBlob = !!cached;
          if (hasCachedBlob === false) {
            cached = URL.createObjectURL(this.blobService.base64ToBlob(image.base64Image));
            this.blobUrlCache.set(key, cached);
            this.blobUrls.push(cached);
          }
          src = cached || "";
        } else if (shouldUseExistingResourceUri === false && image.azureBlobUri) {
          src = image.azureBlobUri;
        }
        return new BqaImageItem({
          azureBlobUri: src || "",
          mediaIdentifier: image.mediaGUID || image.fileIdentifier || "",
          isThumbnailOnly: image.isThumbnailOnly || false
        });
      });


    const jobisComplete = this.isComplete || this.readonly;

    this.dialog.open<string>(ImagesModalComponent, {

      data: {
        images: galleryItems,
        index,
        isComplete: jobisComplete
      },
      backdropClass: "images-modal-backdrop"
    });
  }


  formatFileSize(bytes: number): string {
    if (bytes === 0) {
      return "0 B";
    }
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
  }

  trackByMedia = (_: number, media: Media) =>
    media.id ?? media.fileIdentifier;

  ngOnDestroy(): void {
    // Revoke any blob URLs we created locally
    this.blobUrls.forEach(url => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore
      }
    });
    this.blobUrls = [];
    this.blobUrlCache.clear();
  }

  private getCacheKey(media: Media): string {
    const fid = media.fileIdentifier || "";
    const updatedAtMs = media.updatedAt ? new Date(media.updatedAt).getTime() : 0;
    return `${fid}:${updatedAtMs}`;
  }
}
